/**
 * Google Analytics 4 Event Tracking Utility
 * 
 * This utility provides functions to track custom events in Google Analytics 4.
 * Make sure gtag is loaded via the script in index.html before using these functions.
 */

/**
 * Track a page view
 * @param {string} path - The page path
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    });
  }
};

/**
 * Track quote submission
 * @param {object} quoteData - Quote details
 */
export const trackQuoteSubmission = (quoteData) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'quote_submitted', {
      event_category: 'Engagement',
      event_label: 'Instant Quote',
      material: quoteData.material || 'unknown',
      quality: quoteData.quality || 'unknown',
      estimated_price: quoteData.estimated_price || 0,
    });
  }
};

/**
 * Track order creation
 * @param {object} orderData - Order details
 */
export const trackOrderCreation = (orderData) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'purchase', {
      event_category: 'Ecommerce',
      transaction_id: orderData.order_id || 'unknown',
      value: orderData.total_price || 0,
      currency: 'USD',
      items: [{
        item_id: orderData.quote_id || orderData.product_id || 'unknown',
        item_name: orderData.item_name || 'Print Order',
        item_category: 'Print Service',
        price: orderData.total_price || 0,
        quantity: 1,
      }],
    });
  }
};

/**
 * Track add to cart action
 * @param {object} item - Item details
 */
export const trackAddToCart = (item) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'add_to_cart', {
      event_category: 'Ecommerce',
      currency: 'USD',
      value: item.price || 0,
      items: [{
        item_id: item.id || 'unknown',
        item_name: item.name || item.title || 'Unknown Item',
        item_category: item.type || 'Product',
        price: item.price || 0,
        quantity: 1,
      }],
    });
  }
};

/**
 * Track marketplace design view
 * @param {object} design - Design details
 */
export const trackDesignView = (design) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'view_item', {
      event_category: 'Marketplace',
      event_label: design.title || 'Unknown Design',
      items: [{
        item_id: design.id || 'unknown',
        item_name: design.title || 'Unknown Design',
        item_category: design.category || 'Design',
        price: design.personal_price || 0,
      }],
    });
  }
};

/**
 * Track contact form submission
 * @param {string} formType - Type of contact form
 */
export const trackContactFormSubmission = (formType) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'form_submit', {
      event_category: 'Engagement',
      event_label: formType || 'Contact Form',
    });
  }
};

/**
 * Track service request
 * @param {string} serviceType - Type of service requested
 */
export const trackServiceRequest = (serviceType) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'service_request', {
      event_category: 'Engagement',
      event_label: serviceType || 'Unknown Service',
    });
  }
};

/**
 * Track user authentication
 * @param {string} method - Authentication method (google, email, etc.)
 */
export const trackUserAuth = (method) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'login', {
      method: method || 'unknown',
    });
  }
};

/**
 * Track designer application
 */
export const trackDesignerApplication = () => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'designer_application', {
      event_category: 'Engagement',
      event_label: 'Designer Application Submitted',
    });
  }
};

/**
 * Track design upload
 * @param {object} designData - Design details
 */
export const trackDesignUpload = (designData) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'design_upload', {
      event_category: 'Marketplace',
      event_label: designData.title || 'Unknown Design',
      design_category: designData.category || 'unknown',
    });
  }
};

/**
 * Track custom event
 * @param {string} eventName - Name of the event
 * @param {object} params - Event parameters
 */
export const trackCustomEvent = (eventName, params = {}) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};
